import { useMemo } from 'react'
import { Alert, Box, Card, CardContent, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import { canonicalPeriodLabels, CanonicalPeriod, MetricState, PERIODS } from '../../common'
import { usePropertyClimateData, type PropertyResult } from '../../../../hooks/climateData'
import { HeatStressScoreBadge } from '../../common/heatStress/utils'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'

const formatNumber = (value: number | null, digits = 1) => {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

const getCanonicalValue = (
  metric: PropertyResult | undefined,
  tier: string,
  period: CanonicalPeriod,
): number | null => {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return null
  const tierData = metric.data?.data?.[tier] as
    | Record<string, { x: number; y: number } | { x: number; y: number }[]>
    | undefined
  if (!tierData) return null
  const point = tierData[period]
  if (!point) return null
  if (Array.isArray(point)) {
    const first = point[0]
    return typeof first?.y === 'number' ? first.y : null
  }
  return typeof point?.y === 'number' ? point.y : null
}

type HeatStressOverviewProps = {
  propertyId: number
  emissionTier: string
}

function HeatStressOverview({ propertyId, emissionTier }: HeatStressOverviewProps) {
  const {
    data: heatScores,
    isLoading: heatScoresLoading,
    error: heatScoresError,
  } = usePropertyClimateData('hs:heat_scores', { propertyId })
  const {
    data: hotDays,
    isLoading: hotDaysLoading,
    error: hotDaysError,
  } = usePropertyClimateData('hs:hot_days', { propertyId })

  const overviewRows = useMemo(
    () =>
      PERIODS.map(period => ({
        period,
        label: canonicalPeriodLabels[period],
        heatScore: getCanonicalValue(heatScores, emissionTier, period),
        hotDays: getCanonicalValue(hotDays, emissionTier, period),
      })),
    [emissionTier, heatScores, hotDays],
  )

  const overviewHasData = overviewRows.some(row => row.heatScore != null || row.hotDays != null)
  const overviewLoading = ((heatScoresLoading && !heatScores) || (hotDaysLoading && !hotDays)) && !overviewHasData
  const overviewErrored = !overviewHasData && (heatScoresError != null || hotDaysError != null)

  return (
    <Card>
      <CardContent sx={{ p: 3 }}>
        <Box mb={2}>
          <ClimateSectionTitle title="Overview" subtitle="Heat stress conditions across available horizons" />
        </Box>

        {overviewLoading && (
          <Box display="flex" alignItems="center" justifyContent="center" minHeight={140}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!overviewLoading && overviewHasData && (
          <Grid container spacing={3}>
            {overviewRows.map(row => (
              <Grid item xs={12} md={4} key={row.period}>
                <ClimateMetricCard title={row.label} variant="outlined">
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        Risk Rating
                      </Typography>
                      <Box mt={0.75}>
                        <HeatStressScoreBadge score={row.heatScore ?? null} size="compact" />
                      </Box>
                    </Box>
                    <Box>
                      <Typography variant="body2" color="text.secondary" fontWeight={700}>
                        Hot Days
                      </Typography>
                      <Typography variant="h5">{formatNumber(row.hotDays, 0)} days</Typography>
                    </Box>
                  </Stack>
                </ClimateMetricCard>
              </Grid>
            ))}
          </Grid>
        )}

        {!overviewLoading && !overviewHasData && (
          <Alert severity={overviewErrored ? 'error' : 'info'}>
            {overviewErrored ? 'Unable to load heat stress overview data.' : 'No overview data available.'}
          </Alert>
        )}
      </CardContent>
    </Card>
  )
}

export default HeatStressOverview
